import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { ErrorResponse } from "@reflag/shared/api";
import { CompanyDetail } from "@reflag/shared/companyAPI";

import { companyQueryKeys } from "./companyQueryKeys";

export function useCompanyData<TData = CompanyDetail>(
  companyId?: string,
  options?: Omit<
    UseQueryOptions<CompanyDetail, AxiosError<ErrorResponse>, TData>,
    "enabled" | "queryKey" | "queryFn"
  >,
  envIdOverride?: string,
) {
  const { appId, envId: currentEnvId } = useEnvironment();
  const envId = envIdOverride ?? currentEnvId;

  return useQuery<CompanyDetail, AxiosError<ErrorResponse>, TData>({
    queryKey: companyQueryKeys.single(appId, envId, companyId),

    queryFn: () =>
      api
        .get<"/apps/:appId/companies/:companyId">(
          `/apps/${appId}/companies/${encodeURIComponent(companyId!)}`,
          {
            params: {
              envId: envId!,
            },
          },
        )
        .then((res) => res.data.company),
    retry: (failureCount, error) => {
      if (error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
    enabled: !!appId && !!envId && !!companyId,
  });
}
